import type { DeepStringifyDates } from '@ab/ams-school-backend-utils/src/json-api/types';

// Application
import type { HttpResponse as GetApplicationHttpResponse } from '../../../src/routes/private/v1/applications/{id}/GET/types';
import type { HttpResponse as SpaPropsHttpResponse } from '../../../src/routes/private/v1/spa_props/GET/types';
import type { HttpResponse as ListApplicationsHttpResponse } from '../../../src/routes/private/v1/applications/GET/types';
import type { HttpResponse as ApplicationsPagePropsHttpResponse } from '../../../src/routes/private/v1/applications/page_props/GET/types';
import type {
  HttpRequest as PatchApplicationHttpRequestBody,
  HttpResponse as PatchApplicationHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/PATCH/types';
import type { HttpResponse as SchoolCustomizationHttpResponse } from '../../../src/routes/private/v1/school_customizations/GET/types';
import type { ApplicationResource as BffApplicationResource } from '../../../src/resources/applications/types';
import type { ApplicationsListResource as BffApplicationsListResource } from '../../../src/resources/applications/ApplicationListItem/types';
import type { UniqueApplicationsListResource as BffUniqueApplicationsListResource } from '../../../src/resources/applications/UniqueApplicationListItem';

export type { ApplicationSortableFields } from '../../../src/resources/applications/types';
export type ApplicationResource = BffApplicationResource;
export type ApplicationsListResource = BffApplicationsListResource;
export type UniqueApplicationsListResource = BffUniqueApplicationsListResource;
export type applicationTags = BffApplicationResource['attributes']['tags'][number];
export type Application = BffApplicationResource['attributes'] & { id: string };
export type Attachment = BffApplicationResource['attributes']['supportingDocuments'][number];

export type GetApplicationResponse = GetApplicationHttpResponse['body'];
export type SpaPropsResponse = SpaPropsHttpResponse['body'];
export type PatchApplicationResponse = PatchApplicationHttpResponse['body'];
export type ApplicationsPagePropsResponse = ApplicationsPagePropsHttpResponse['body'];
export type PatchApplicationRequestBody = PatchApplicationHttpRequestBody['body'];
export type ListApplicationsResponse = ListApplicationsHttpResponse['body'];
export type SchoolCustomizationHttpResponseBody = SchoolCustomizationHttpResponse['body'];

// Intakes
import type { HttpResponse as ListIntakesHttpResponse } from '../../../src/routes/private/v1/intakes_search/GET/types';
import type { HttpResponse as IntakeManagementPagePropsHttpResponse } from '../../../src/routes/private/v1/intakes/page_props/GET/types';
export type {
  HttpResponse as IntakesBulkPatchResponse,
  HttpRequest as IntakesBulkPatchRequest,
} from '../../../src/routes/private/v1/intakes/bulk/PATCH/types';
import type { ProgramIntakeSearchResource } from '../../../src/resources/programIntakes/types';
export type { ProgramIntakeSortColumn } from '../../../src/resources/programIntakes/types';
export type ListIntakesResponse = ListIntakesHttpResponse['body'];
export type IntakesListResource = ProgramIntakeSearchResource;
export type IntakeManagementPagePropsResponse = IntakeManagementPagePropsHttpResponse['body'];

// Letter Preview
import type {
  HttpRequest as GetPreOfferLetterPreviewHttpRequestBody,
  HttpResponse as GetPreOfferLetterPreviewHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/pre_offer_letter_preview/POST/types';
export type GetPreOfferLetterPreviewRequestBody = DeepStringifyDates<GetPreOfferLetterPreviewHttpRequestBody['body']>;
export type GetPreOfferLetterPreviewResponse = DeepStringifyDates<GetPreOfferLetterPreviewHttpResponse['body']>;

import type {
  HttpRequest as GetWaitlistLetterPreviewHttpRequestBody,
  HttpResponse as GetWaitlistLetterPreviewHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/waitlist_letter_preview/POST/types';
export type GetWaitlistLetterPreviewRequestBody = DeepStringifyDates<GetWaitlistLetterPreviewHttpRequestBody['body']>;
export type GetWaitlistLetterPreviewResponse = DeepStringifyDates<GetWaitlistLetterPreviewHttpResponse['body']>;

import type {
  HttpRequest as GetFinalOfferLetterPreviewHttpRequestBody,
  HttpResponse as GetFinalOfferLetterPreviewHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/final_offer_letter_preview/POST/types';
export type GetFinalOfferLetterPreviewRequestBody = DeepStringifyDates<GetFinalOfferLetterPreviewHttpRequestBody['body']>;
export type GetFinalOfferLetterPreviewResponse = DeepStringifyDates<GetFinalOfferLetterPreviewHttpResponse['body']>;

import type {
  HttpRequest as GetRejectionLetterPreviewHttpRequestBody,
  HttpResponse as GetRejectionLetterPreviewHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/rejection_letter_preview/POST/types';
export type GetRejectionLetterPreviewRequestBody = GetRejectionLetterPreviewHttpRequestBody['body'];
export type GetRejectionLetterPreviewResponse = GetRejectionLetterPreviewHttpResponse['body'];

// Programs
import type { HttpResponse as ListProgramsHttpResponse } from '../../../src/routes/private/v1/programs/GET/types';
import type { HttpResponse as GetProgramHttpResponse } from '../../../src/routes/private/v1/programs/{id}/GET/types';
export type { HttpResponse as ProgramsPagePropsHttpResponse } from '../../../src/routes/private/v1/programs/page_props/GET/types';
export type { ProgramResource } from '../../../src/resources/programs/types';
export type ListProgramsResponse = ListProgramsHttpResponse['body'];
export type GetProgramResponse = GetProgramHttpResponse['body'];

// Courses
import type { HttpResponse as ListCoursesHttpResponse } from '../../../src/routes/private/v1/courses/GET/types';
export type { CourseResource, Course, CourseSortColumn } from '../../../src/resources/courses/types';
export type ListCoursesResponse = ListCoursesHttpResponse['body'];

// Application Admission Conditions
import type { HttpResponse as ListApplicationAdmissionConditionsResponses } from '../../../src/routes/private/v1/admission_conditions/GET/types';
export type { ApplicationAdmissionConditionResource } from '../../../src/resources/applicationAdmissionConditions/types';
export type ListApplicationAdmissionConditionsResponse = ListApplicationAdmissionConditionsResponses['body'];

// Application Final Offer Conditions
import type { HttpResponse as ListApplicationFinalOfferConditionsResponses } from '../../../src/routes/private/v1/final_offer_conditions/GET/types';
export type { ApplicationFinalOfferConditionResource } from '../../../src/resources/applicationFinalOfferConditions/types';
export type ListApplicationFinalOfferConditionsResponse = ListApplicationFinalOfferConditionsResponses['body'];

// Application Rejection Reasons
import type { HttpResponse as ListApplicationRejectionReasonses } from '../../../src/routes/private/v1/rejection_reasons/GET/types';
export type { ApplicationRejectionReasonResource } from '../../../src/resources/applicationRejectionReasons/types';
export type ListApplicationRejectionReasons = ListApplicationRejectionReasonses['body'];

// Additional Document Request Types
export type { AdditionalDocumentsRequest, AdditionalDocumentsRequestResource } from '../../../src/resources/applications/types';

import type {
  HttpRequest as PostAdditionalDocumentHttpRequest,
  HttpResponse as PostAdditionalDocumentHttpRequestResponse,
} from '../../../src/routes/private/v1/applications/{id}/additional_document_requests/POST/types';
export type { ApplicationAdditionalDocumentsRequestResource } from '../../../src/routes/private/v1/applications/{id}/additional_document_requests/POST/types';
export type PostAdditionalDocumentRequestBody = PostAdditionalDocumentHttpRequest['body'];
export type PostAdditionalDocumentRequestResponse = PostAdditionalDocumentHttpRequestResponse['body'];

// reports
export type { ReportResource } from './../../../src/resources/reports/types';
import type { HttpResponse as GetApplicationReportsHttpResponse } from './../../../src/routes/private/v1/reports/applications/GET/types';
export type GetApplicationReportsResponse = GetApplicationReportsHttpResponse['body'];

// Deferral Request Type
export type { DeferralRequest } from '../../../src/resources/applications/deferralRequest/types';

// Application Waitlist Conditions
import type { HttpResponse as ListApplicationWaitlistConditionsResponses } from '../../../src/routes/private/v1/waitlist_conditions/GET/types';
export type { ApplicationWaitlistConditionResource } from '../../../src/resources/applicationWaitlistConditions/types';
export type ListApplicationWaitlistConditionsResponse = ListApplicationWaitlistConditionsResponses['body'];

import type {
  HttpRequest as GetCustomizedDocumentTypesRequestObject,
  HttpResponse as GetCustomizedDocumentTypesResponseObject,
} from '../../../src/routes/private/v1/customized_document_types/GET/types';
export type GetCustomizedDocumentTypesHttpRequest = GetCustomizedDocumentTypesRequestObject;
export type GetCustomizedDocumentTypesHttpResponse = GetCustomizedDocumentTypesResponseObject['body'];

import type {
  HttpRequest as PatchDeferralHttpRequestBody,
  HttpResponse as PatchDeferralHttpRequestResponse,
} from '../../../src/routes/private/v1/applications/{id}/deferral_request/PATCH/types';

export type PatchDeferralRequestBody = PatchDeferralHttpRequestBody['body'];
export type PatchDeferralRequestResponse = PatchDeferralHttpRequestResponse['body'];

import type {
  HttpRequest as PatchStudentHttpRequestBody,
  HttpResponse as PatchStudentHttpResponse,
} from '../../../src/routes/private/v1/students/{id}/PATCH/types';
export type PatchStudentRequestBody = PatchStudentHttpRequestBody['body'];
export type PatchStudentResponse = PatchStudentHttpResponse['body'];

import type {
  HttpRequest as PutOfferHttpRequestBody,
  HttpResponse as PutOfferHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/offer/PUT/types';
export type PutOfferRequestBody = PutOfferHttpRequestBody['body'];
export type PutOfferResponse = PutOfferHttpResponse['body'];

import type {
  HttpRequest as PutGpaCalculationParametersHttpRequestBody,
  HttpResponse as PutGpaCalculationParametersHttpResponse,
} from '../../../src/routes/private/v1/applications/{id}/gpa_calculation_parameters/PUT/types';
export type PutGpaCalculationParametersRequestBody = PutGpaCalculationParametersHttpRequestBody['body'];
export type PutGpaCalculationParametersResponse = PutGpaCalculationParametersHttpResponse['body'];

// Language Proficiency
// we cannot expose @applyboard/school-applications-types-lib to the frontend, there are references to backend-utils in
// types-lib, which will crash the frontend
// export { TestType, HasLanguageProficiencyCodes } from '@applyboard/school-applications-types-lib';
export enum TestType {
  IELTS = 'IELTS',
  PTE = 'PTE',
  TOEFL = 'TOEFL',
  OTHER = 'OTHER',
  DUOLINGO = 'DUOLINGO',
  CAEL = 'CAEL',
  CAMBRIDGE = 'CAMBRIDGE',
  MET = 'MET',
  EPP = 'EPP',
  CANTEST = 'CANTEST',
  LANGUAGECERT_ESOL = 'LANGUAGECERT_ESOL',
  LEAP_4_ADVANCED = 'LEAP_4_ADVANCED',
}

export enum HasLanguageProficiencyCodes {
  HAVE = 'HAVE',
  DONT_NEED = 'DONT_NEED',
  WILL_HAVE = 'WILL_HAVE',
}

// Agencies
import type { HttpResponse as ListAgenciesHttpResponse } from '../../../src/routes/private/v1/agencies/GET/types';
export type { AgencyResource } from '../../../src/resources/agencies/types';
export type ListAgenciesResponse = ListAgenciesHttpResponse['body'];
